import {
  Box,
  Button,
  Drawer,
  IconButton,
  Input,
  Sheet,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Actionnable_Entity } from "../../entities/actionnable";
import { Hook_Entity } from "../../entities/hook";
import { Option_Entity } from "../../entities/option";
import { Trigger_Entity } from "../../entities/trigger";
import {
  formOpenSelector,
  selectedHookIDSelector,
  setFormOpen,
  setSelectedHookID,
} from "../../reducers/rhapsody";
import {
  useCreateHookMutation,
  useDeleteHookMutation,
  useUpdateHookMutation,
} from "../../redux/hook/hookEndpoints";
import { useHook } from "../../redux/hook/hookHooks";
import { autopilotApi } from "../../redux/api/autopilotApi";
import { useTriggers } from "src/redux/triggers/triggerHooks";
import {
  useCreateActionMutation,
  useDeleteActionMutation,
  useUpdateActionMutation,
} from "src/redux/action/actionEndpoints";
import {
  useCreateTriggerMutation,
  useDeleteTriggerMutation,
  useUpdateTriggerMutation,
} from "src/redux/triggers/triggerEndpoints";
import {
  useCreateOptionMutation,
  useDeleteOptionMutation,
  useUpdateOptionMutation,
} from "src/redux/option/optionEndpoints";

type WithTempId<T> = Partial<T> & { tempId: string };

type EditableHook = Omit<
  Partial<Hook_Entity>,
  "actions" | "triggers" | "options"
> & {
  actions: Array<WithTempId<Actionnable_Entity>>;
  triggers: Array<WithTempId<Trigger_Entity>>;
  options: Array<WithTempId<Option_Entity>>;
};

const createEmptyHook = (): EditableHook => ({
  name: "",
  description: "",
  actions: [],
  options: [],
  triggers: [],
});

const createTempId = () => Math.random().toString(36).slice(2);

const normalizeCollection = <T extends { id?: number }>(
  collection?: Array<Partial<T>>
): Array<WithTempId<T>> =>
  collection?.map((item) => ({
    ...item,
    tempId: item?.id != null ? `persisted-${item.id}` : createTempId(),
  })) ?? [];

type CollectionState = {
  actions: Array<WithTempId<Actionnable_Entity>>;
  triggers: Array<WithTempId<Trigger_Entity>>;
  options: Array<WithTempId<Option_Entity>>;
};

const createEmptyCollections = (): CollectionState => ({
  actions: [],
  triggers: [],
  options: [],
});

/**
 *
 * @returns {ReactElement} HookDrawer panel
 */
export function HookDrawer() {
  const open = useSelector(formOpenSelector("hook"));
  const hookID = useSelector(selectedHookIDSelector);
  const { triggers } = useTriggers();
  const hookTriggers = useMemo(
    () =>
      (triggers ?? []).filter((trigger) =>
        hookID ? trigger.hookID === hookID : false
      ),
    [triggers, hookID]
  );
  const { hook, isFetching } = useHook(hookID, { skip: !hookID });
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<EditableHook>(() => createEmptyHook());
  const [initialCollections, setInitialCollections] = useState<CollectionState>(
    () => createEmptyCollections()
  );
  const [activeTab, setActiveTab] = useState<
    "actions" | "triggers" | "options"
  >("actions");
  const [syncingCollections, setSyncingCollections] = useState(false);
  const [createHook, { isLoading: creating }] = useCreateHookMutation();
  const [updateHook, { isLoading: updating }] = useUpdateHookMutation();
  const [deleteHook, { isLoading: deleting }] = useDeleteHookMutation();

  const [createAction] = useCreateActionMutation();
  const [updateAction] = useUpdateActionMutation();
  const [deleteAction] = useDeleteActionMutation();

  const [createTrigger] = useCreateTriggerMutation();
  const [updateTrigger] = useUpdateTriggerMutation();
  const [deleteTrigger] = useDeleteTriggerMutation();

  const [createOption] = useCreateOptionMutation();
  const [updateOption] = useUpdateOptionMutation();
  const [deleteOption] = useDeleteOptionMutation();

  useEffect(() => {
    if (!open) return;

    if (hookID && hook?.id === hookID) {
      const normalizedActions = normalizeCollection<Actionnable_Entity>(
        hook.actions
      );
      const normalizedOptions = normalizeCollection<Option_Entity>(
        hook.options
      );
      const normalizedTriggers =
        normalizeCollection<Trigger_Entity>(hookTriggers);

      setDraft({
        ...createEmptyHook(),
        ...hook,
        actions: normalizedActions,
        triggers: normalizedTriggers,
        options: normalizedOptions,
      });
      setInitialCollections({
        actions: normalizedActions,
        triggers: normalizedTriggers,
        options: normalizedOptions,
      });
    } else if (!hookID) {
      const empty = createEmptyHook();
      setDraft(empty);
      setInitialCollections(createEmptyCollections());
    }
    setActiveTab("actions");
  }, [hookID, hook, hookTriggers, open]);

  const closeDrawer = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "hook" }));
    dispatch(setSelectedHookID(null));
  };

  const updateField = <K extends keyof EditableHook>(
    field: K,
    value: EditableHook[K]
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toPayload = (): Partial<Hook_Entity> => ({
    name: draft.name?.trim(),
    description: draft.description ?? "",
  });

  const handleSave = async () => {
    if (syncingCollections) return;
    const payload = toPayload();
    if (!payload.name) return;
    setSyncingCollections(true);
    try {
      let hookId = draft.id;
      if (hookId) {
        await updateHook({ id: hookId, body: payload }).unwrap();
      } else {
        const createdHook = await createHook(payload).unwrap();
        hookId = createdHook.id;
      }
      if (!hookId) throw new Error("Failed to determine hook ID");
      await syncActions(hookId);
      await syncTriggers(hookId);
      await syncOptions(hookId);
      // The action/option mutations only invalidate their own string tags, not
      // getHook(id) (which embeds hook.actions/hook.options). Refresh the hook
      // itself so reopening it shows the saved children instead of stale data.
      dispatch(
        autopilotApi.util.invalidateTags([{ type: "hooks", id: hookId }])
      );
      closeDrawer();
    } catch (error) {
      console.error("Failed to save hook", error);
    } finally {
      setSyncingCollections(false);
    }
  };

  const handleDelete = async () => {
    if (!draft.id || syncingCollections) return;
    setSyncingCollections(true);
    try {
      await Promise.all([
        ...draft.actions
          .filter((a) => a.id != null)
          .map((action) => deleteAction(action.id as number)),
        ...draft.triggers
          .filter((t) => t.id != null)
          .map((trigger) => deleteTrigger(trigger.id as number)),
        ...draft.options
          .filter((o) => o.id != null)
          .map((option) => deleteOption(option.id as number)),
      ]);
      await deleteHook(draft.id).unwrap();
      closeDrawer();
    } catch (error) {
      console.error("Failed to delete hook", error);
    } finally {
      setSyncingCollections(false);
    }
  };

  const buildActionPayload = (
    action: WithTempId<Actionnable_Entity>,
    hookId: number
  ): Partial<Actionnable_Entity> => ({
    name: action.name ?? "",
    description: action.description ?? "",
    icon: action.icon ?? "",
    callback: action.callback ?? "",
    parentActionID: action.parentActionID,
    relationID: action.relationID ?? hookId,
  });

  const buildTriggerPayload = (
    trigger: WithTempId<Trigger_Entity>,
    hookId: number
  ): Partial<Trigger_Entity> => ({
    event: trigger.event ?? "",
    timedelay: typeof trigger.timedelay === "number" ? trigger.timedelay : 0,
    hookID: hookId,
  });

  const buildOptionPayload = (
    option: WithTempId<Option_Entity>,
    hookId: number
  ): Partial<Option_Entity> => ({
    name: option.name ?? "",
    description: option.description ?? "",
    value: option.value ?? "",
    relationID: option.relationID ?? hookId,
  });

  const syncActions = async (hookId: number) => {
    const { toCreate, toDelete, toUpdate } = diffCollections(
      initialCollections.actions,
      draft.actions
    );
    await Promise.all(
      toDelete
        .filter((action) => action.id != null)
        .map((action) => deleteAction(action.id as number))
    );
    await Promise.all(
      toUpdate
        .filter((action) => action.id != null)
        .map((action) =>
          updateAction({
            id: action.id as number,
            body: buildActionPayload(action, hookId),
          })
        )
    );
    await Promise.all(
      toCreate.map((action) => createAction(buildActionPayload(action, hookId)))
    );
  };

  const syncTriggers = async (hookId: number) => {
    const { toCreate, toDelete, toUpdate } = diffCollections(
      initialCollections.triggers,
      draft.triggers
    );
    await Promise.all(
      toDelete
        .filter((trigger) => trigger.id != null)
        .map((trigger) => deleteTrigger(trigger.id as number))
    );
    await Promise.all(
      toUpdate
        .filter((trigger) => trigger.id != null)
        .map((trigger) =>
          updateTrigger({
            id: trigger.id as number,
            body: buildTriggerPayload(trigger, hookId),
          })
        )
    );
    await Promise.all(
      toCreate.map((trigger) =>
        createTrigger(buildTriggerPayload(trigger, hookId))
      )
    );
  };

  const syncOptions = async (hookId: number) => {
    const { toCreate, toDelete, toUpdate } = diffCollections(
      initialCollections.options,
      draft.options
    );
    await Promise.all(
      toDelete
        .filter((option) => option.id != null)
        .map((option) => deleteOption(option.id as number))
    );
    await Promise.all(
      toUpdate
        .filter((option) => option.id != null)
        .map((option) =>
          updateOption({
            id: option.id as number,
            body: buildOptionPayload(option, hookId),
          })
        )
    );
    await Promise.all(
      toCreate.map((option) => createOption(buildOptionPayload(option, hookId)))
    );
  };

  const headerTitle = useMemo(() => {
    if (draft.name?.trim()) return draft.name;
    if (draft.id) return `Hook #${draft.id}`;
    return "New Hook";
  }, [draft.id, draft.name]);

  const disableSave =
    !draft.name?.trim() || creating || updating || syncingCollections;

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      anchor="right"
      sx={{ width: 320 }}
    >
      <Sheet
        sx={{
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          height: "100%",
        }}
      >
        <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
          <Typography level="title-lg">{headerTitle}</Typography>
          <Input
            placeholder="Name"
            value={draft.name ?? ""}
            onChange={(e) => updateField("name", e.target.value)}
            disabled={isFetching}
            autoFocus
          />
          <Textarea
            placeholder="Description"
            minRows={3}
            value={draft.description ?? ""}
            onChange={(e) => updateField("description", e.target.value)}
            disabled={isFetching}
          />
          <Tabs
            value={activeTab}
            onChange={(_event, value) =>
              setActiveTab((value as typeof activeTab) ?? "actions")
            }
            sx={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <TabList>
              <Tab value="actions">Actions</Tab>
              <Tab value="triggers">Triggers</Tab>
              <Tab value="options">Options</Tab>
            </TabList>
            <TabPanel
              value="actions"
              sx={{ flex: 1, overflow: "auto", p: 0, pt: 2 }}
            >
              <CollectionEditor
                items={draft.actions}
                onChange={(items) => updateField("actions", items)}
                emptyLabel="No actions configured."
                addLabel="Add Action"
                onAdd={() =>
                  updateField("actions", [
                    ...draft.actions,
                    {
                      tempId: createTempId(),
                      name: "",
                      description: "",
                      callback: "",
                    },
                  ])
                }
                renderItem={(item, onItemChange) => (
                  <Stack spacing={1}>
                    <Input
                      placeholder="Name"
                      value={item.name ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, name: e.target.value })
                      }
                    />
                    <Textarea
                      minRows={2}
                      placeholder="Description"
                      value={item.description ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, description: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Callback"
                      value={item.callback ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, callback: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Icon"
                      value={item.icon ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, icon: e.target.value })
                      }
                    />
                  </Stack>
                )}
              />
            </TabPanel>
            <TabPanel
              value="triggers"
              sx={{ flex: 1, overflow: "auto", p: 0, pt: 2 }}
            >
              <CollectionEditor
                items={draft.triggers}
                onChange={(items) => updateField("triggers", items)}
                emptyLabel="No triggers configured."
                addLabel="Add Trigger"
                onAdd={() =>
                  updateField("triggers", [
                    ...draft.triggers,
                    { tempId: createTempId(), event: "", timedelay: 0 },
                  ])
                }
                renderItem={(item, onItemChange) => (
                  <Stack spacing={1}>
                    <Input
                      placeholder="Event"
                      value={item.event ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, event: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Delay (ms)"
                      type="number"
                      value={
                        item.timedelay != null ? String(item.timedelay) : ""
                      }
                      onChange={(e) => {
                        const nextValue =
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value);
                        onItemChange({
                          ...item,
                          timedelay: Number.isNaN(nextValue)
                            ? undefined
                            : nextValue,
                        });
                      }}
                    />
                  </Stack>
                )}
              />
            </TabPanel>
            <TabPanel
              value="options"
              sx={{ flex: 1, overflow: "auto", p: 0, pt: 2 }}
            >
              <CollectionEditor
                items={draft.options}
                onChange={(items) => updateField("options", items)}
                emptyLabel="No options configured."
                addLabel="Add Option"
                onAdd={() =>
                  updateField("options", [
                    ...draft.options,
                    {
                      tempId: createTempId(),
                      name: "",
                      description: "",
                      value: "",
                    },
                  ])
                }
                renderItem={(item, onItemChange) => (
                  <Stack spacing={1}>
                    <Input
                      placeholder="Name"
                      value={item.name ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, name: e.target.value })
                      }
                    />
                    <Textarea
                      minRows={2}
                      placeholder="Description"
                      value={item.description ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, description: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Value"
                      value={item.value ?? ""}
                      onChange={(e) =>
                        onItemChange({ ...item, value: e.target.value })
                      }
                    />
                  </Stack>
                )}
              />
            </TabPanel>
          </Tabs>
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {draft.id ? (
            <Button
              color="danger"
              variant="soft"
              onClick={handleDelete}
              loading={deleting || syncingCollections}
            >
              Delete
            </Button>
          ) : null}
          <Button variant="plain" onClick={closeDrawer}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={disableSave}
            loading={creating || updating || syncingCollections}
          >
            {draft.id ? "Save hook" : "Create hook"}
          </Button>
        </Stack>
      </Sheet>
    </Drawer>
  );
}

type CollectionEditorProps<T extends { tempId: string }> = {
  items: T[];
  emptyLabel: string;
  addLabel: string;
  onAdd: () => void;
  onChange: (items: T[]) => void;
  renderItem: (item: T, onItemChange: (nextItem: T) => void) => ReactNode;
};

/**
 * @param root0
 * @param root0.items
 * @param root0.emptyLabel
 * @param root0.addLabel
 * @param root0.onAdd
 * @param root0.onChange
 * @param root0.renderItem
 */
function CollectionEditor<T extends { tempId: string }>({
  items,
  emptyLabel,
  addLabel,
  onAdd,
  onChange,
  renderItem,
}: CollectionEditorProps<T>) {
  const updateItem = (index: number, nextItem: T) => {
    const next = [...items];
    next[index] = nextItem;
    onChange(next);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={1.5}>
      {items.length === 0 ? (
        <Typography level="body-sm" color="neutral">
          {emptyLabel}
        </Typography>
      ) : (
        items.map((item, index) => (
          <Sheet
            key={item.tempId}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: "md",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Stack direction="row" alignItems="center">
              <Typography level="title-sm">#{index + 1}</Typography>
              <Box flex={1} />
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => removeItem(index)}
              >
                <i className="fa-solid fa-trash" />
              </IconButton>
            </Stack>
            {renderItem(item, (next) => updateItem(index, next))}
          </Sheet>
        ))
      )}
      <Button size="sm" variant="outlined" onClick={onAdd}>
        {addLabel}
      </Button>
    </Stack>
  );
}

/**
 * @param previous
 * @param next
 */
function diffCollections<T extends { id?: number }>(
  previous: Array<WithTempId<T>>,
  next: Array<WithTempId<T>>
) {
  const toDelete = previous.filter(
    (prev) => prev.id != null && !next.find((item) => item.id === prev.id)
  );
  const toUpdate = next.filter((item) => item.id != null);
  const toCreate = next.filter((item) => item.id == null);
  return { toCreate, toDelete, toUpdate };
}
